import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useCallback } from 'react'
import {
  CalendarAttendeeRequest,
  CalendarAttendeeResponse,
  CalendarAttendeeResponseSchema,
  JobCalendarRequest,
  JobCalendarResponse,
  JobCalendarResponseSchema,
} from 'types'

// API endpoint constants
const JOB_CALENDAR_ENDPOINT = '/api/v1/alerts/job-calendar'
const CALENDAR_ATTENDEES_ADD_ENDPOINT = '/api/v1/alerts/calendar-attendees/add'
const CALENDAR_ATTENDEES_REMOVE_ENDPOINT =
  '/api/v1/alerts/calendar-attendees/remove'

export const useJobCalendar = () => {
  const { authorizedAxios: axios } = useGeckoAuth()
  const { ALERTS_URL } = useRuntimeVariables()

  const upsertCalendarEvent = useCallback(
    async (request: JobCalendarRequest): Promise<JobCalendarResponse> => {
      if (!ALERTS_URL) {
        throw new Error('Alerts URL is not configured')
      }

      try {
        const endpoint = `${ALERTS_URL}${JOB_CALENDAR_ENDPOINT}`
        const response = await axios.put(endpoint, request)
        return JobCalendarResponseSchema.parse(response.data)
      } catch (error) {
        if (error instanceof Error) {
          throw error
        }
        throw new Error('Failed to upsert calendar event')
      }
    },
    [axios, ALERTS_URL]
  )

  const deleteCalendarEvent = useCallback(
    async (eventId: string, jobId: string): Promise<JobCalendarResponse> => {
      if (!ALERTS_URL) {
        throw new Error('Alerts URL is not configured')
      }

      try {
        const endpoint = `${ALERTS_URL}${JOB_CALENDAR_ENDPOINT}/${eventId}`

        const response = await axios.delete(endpoint, {
          params: { job_id: jobId },
        })
        return JobCalendarResponseSchema.parse(response.data)
      } catch (error) {
        if (error instanceof Error) {
          throw error
        }
        throw new Error('Failed to delete calendar event')
      }
    },
    [axios, ALERTS_URL]
  )

  const addCalendarAttendees = useCallback(
    async (
      request: CalendarAttendeeRequest
    ): Promise<CalendarAttendeeResponse> => {
      if (!ALERTS_URL) {
        throw new Error('Alerts URL is not configured')
      }

      try {
        const endpoint = `${ALERTS_URL}${CALENDAR_ATTENDEES_ADD_ENDPOINT}`
        const response = await axios.post(endpoint, request)
        return CalendarAttendeeResponseSchema.parse(response.data)
      } catch (error) {
        if (error instanceof Error) {
          throw error
        }
        throw new Error('Failed to add calendar attendees')
      }
    },
    [axios, ALERTS_URL]
  )

  const removeCalendarAttendees = useCallback(
    async (
      request: CalendarAttendeeRequest
    ): Promise<CalendarAttendeeResponse> => {
      if (!ALERTS_URL) {
        throw new Error('Alerts URL is not configured')
      }

      try {
        const endpoint = `${ALERTS_URL}${CALENDAR_ATTENDEES_REMOVE_ENDPOINT}`
        const response = await axios.post(endpoint, request)
        return CalendarAttendeeResponseSchema.parse(response.data)
      } catch (error) {
        if (error instanceof Error) {
          throw error
        }
        throw new Error('Failed to remove calendar attendees')
      }
    },
    [axios, ALERTS_URL]
  )

  return {
    upsertCalendarEvent,
    deleteCalendarEvent,
    addCalendarAttendees,
    removeCalendarAttendees,
  }
}
